const SUPPORTED = 'SUPPORTED';
const RESTRICTED = 'RESTRICTED';
const OUTDATED = 'OUTDATED';

export const browserSupportStatus = {
  SUPPORTED, // WebRTC should be working fine
  RESTRICTED, // WebRTC was disabled
  OUTDATED, // Browser version doesn't support WebRTC well enough
};

const MIN_CHROME_VERSION = 69;
const MIN_FIREFOX_VERSION = 60;
const MIN_SAFARI_VERSION = 13;
const MIN_EDGE_VERSION = 80;

export const SUPPORTED_BROWSER_VERSIONS = {
  chrome: MIN_CHROME_VERSION,
  firefox: MIN_FIREFOX_VERSION,
  safari: MIN_SAFARI_VERSION,
  edge: MIN_EDGE_VERSION,
};

/**
 * Returns the supported browser list according to the OS
 * While it is implicitly supported, we do not recommend Edge yet.
 */
export const getOsSupportedBrowsers = (os = getOS()) => {
  const allSupportedBrowserList = Object.keys(SUPPORTED_BROWSER_VERSIONS);
  const isOsSupportedBrowser = (browserName) => allSupportedBrowserList.includes(browserName);
  switch (os) {
    case 'iOS':
      return ['safari'].filter(isOsSupportedBrowser);
    case 'Linux':
    case 'Windows':
      return ['chrome', 'firefox', 'edge'].filter(isOsSupportedBrowser);
    case 'Android':
      return ['chrome'].filter(isOsSupportedBrowser);
    case 'MacOS':
    default:
      return ['safari', 'chrome', 'firefox', 'edge'].filter(isOsSupportedBrowser);
  }
};

// From https://stackoverflow.com/questions/38241480/detect-macos-ios-windows-android-and-linux-os-with-js
/**
 *
 * @returns {null|String}
 */
const getOS = () => {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  const isIPad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  let os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'MacOS';
  } else if (isIPad || iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
  } else if (/Android/.test(userAgent)) {
    os = 'Android';
  } else if (/Linux/.test(platform)) {
    os = 'Linux';
  }

  return os;
};

/**
 * Browser detector.
 */
export function detectBrowser(window) {
  const { navigator, RTCPeerConnection } = window;

  // Fail early if it's not a browser
  if (typeof window === 'undefined' || !window.navigator) {
    const result = { browser: null, version: null };
    result.browser = 'Not a browser.';
    return result;
  }

  // User agent sniffing inspired by https://dev.to/shwetadanej/detect-browser-and-its-version-in-javascript-dg9
  const navUserAgent = navigator.userAgent;
  let browserName = navigator.appName;
  let browserVersion = '' + parseFloat(navigator.appVersion);
  let tempNameOffset, tempVersionOffset, tempVersion;

  if ((navUserAgent.indexOf('Opera')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('Opera');
    browserName = 'Opera';
    browserVersion = navUserAgent.substring(tempVersionOffset + 6);
    if ((navUserAgent.indexOf('Version')) !== -1) {
      tempVersionOffset = navUserAgent.indexOf('Version');
      browserVersion = navUserAgent.substring(tempVersionOffset + 8);
    }
  } else if ((navUserAgent.indexOf('MSIE')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('MSIE');
    browserName = 'Microsoft Internet Explorer';
    browserVersion = navUserAgent.substring(tempVersionOffset + 5);
  } else if ((navUserAgent.indexOf('Edg')) !== -1) {
    if (navUserAgent.indexOf('Edge') !== -1) {
      tempVersionOffset = navUserAgent.indexOf('Edge') + 5;
    } else {
      tempVersionOffset = navUserAgent.indexOf('Edg') + 4;
    }
    browserName = 'Edge';
    browserVersion = navUserAgent.substring(tempVersionOffset);
  } else if ((navUserAgent.indexOf('Chrome')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('Chrome');
    browserName = 'Chrome';
    browserVersion = navUserAgent.substring(tempVersionOffset + 7);
  } else if ((navUserAgent.indexOf('CriOS')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('CriOS');
    browserName = 'Chrome';
    browserVersion = navUserAgent.substring(tempVersionOffset + 1);
  } else if ((navUserAgent.indexOf('FxiOS')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('FxiOS');
    browserName = 'Firefox';
    browserVersion = navUserAgent.substring(tempVersionOffset + 6);
  } else if ((navUserAgent.indexOf('Safari')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('Safari');
    browserName = 'Safari';
    browserVersion = navUserAgent.substring(tempVersionOffset + 7);
    if ((navUserAgent.indexOf('Version')) !== -1) {
      tempVersionOffset = navUserAgent.indexOf('Version');
      browserVersion = navUserAgent.substring(tempVersionOffset + 8);
    }
  } else if ((navUserAgent.indexOf('Firefox')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('Firefox');
    browserName = 'Firefox';
    browserVersion = navUserAgent.substring(tempVersionOffset + 8);
  } else if ((tempNameOffset = navUserAgent.lastIndexOf(' ') + 1) < (tempVersionOffset = navUserAgent.lastIndexOf('/'))) {
    browserName = navUserAgent.substring(tempNameOffset, tempVersionOffset);
    browserVersion = navUserAgent.substring(tempVersionOffset + 1);
    if (browserName.toLowerCase() === browserName.toUpperCase()) {
      browserName = navigator.appName;
    }
  }

  // trim version
  if ((tempVersion = browserVersion.indexOf(';')) !== -1) {
    browserVersion = browserVersion.substring(0, tempVersion);
  }
  if ((tempVersion = browserVersion.indexOf(' ')) !== -1) {
    browserVersion = browserVersion.substring(0, tempVersion);
  }

  return {
    browser: browserName.toLowerCase(),
    browserName,
    fullVersion: browserVersion,
    version: browserVersion.split('.')[0],
    webRTCsupport: typeof navigator.mediaDevices === 'object' &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof RTCPeerConnection === 'function',
    os: getOS(),
  };
}

export const navigatorDetails = detectBrowser(window);
